import { Line } from 'react-chartjs-2';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDecimal } from '../../../../utils/formatForecastNumber';

export const buildChartData = (points, labels) => ({
    labels: points.map((point) => point.label),
    datasets: [
        {
            label: labels.spent,
            data: points.map((point) => point.spent),
            fill: false,
            backgroundColor: 'rgb(255, 99, 132)',
            borderColor: 'rgba(255, 99, 132, 0.7)',
            yAxisID: 'y-axis-spent',
        },
        {
            label: labels.impacts,
            data: points.map((point) => point.impacts),
            fill: true,
            backgroundColor: 'rgba(54, 162, 235, 0.3)',
            borderColor: 'rgba(54, 162, 235, 0.2)',
            yAxisID: 'y-axis-impacts',
        },
    ],
});

export const buildChartOptions = (labels, currencySymbol = '') => ({
    maintainAspectRatio: false,
    responsive: true,
    tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
            label: (tooltipItem, data) => {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const prefix =
                    dataset.yAxisID === 'y-axis-spent' ? currencySymbol : '';
                return `${dataset.label}: ${prefix}${formatDecimal(
                    dataset.data[tooltipItem.index]
                )}`;
            },
        },
    },
    scales: {
        yAxes: [
            {
                id: 'y-axis-spent',
                type: 'linear',
                position: 'left',
                scaleLabel: {
                    display: true,
                    labelString: labels.spent,
                },
                ticks: {
                    beginAtZero: true,
                    callback: (value) => `${currencySymbol}${formatDecimal(value)}`,
                },
            },
            {
                id: 'y-axis-impacts',
                type: 'linear',
                position: 'right',
                scaleLabel: {
                    display: true,
                    labelString: labels.impacts,
                },
                gridLines: { drawOnChartArea: false },
                ticks: { beginAtZero: true },
            },
        ],
    },
});

const ForecastDistributionChart = ({
    points = [],
    isLoading = false,
    currencySymbol = '',
}) => {
    const { t } = useTranslation();
    const translationRoot =
        'sections.campaigns.new-campaign.wizard-audiences.forecast';
    const spentLabel = t('common.words.spent');
    const impactsLabel = t(`${translationRoot}.data-table.impacts`);
    const data = useMemo(
        () =>
            buildChartData(points, {
                spent: spentLabel,
                impacts: impactsLabel,
            }),
        [points, spentLabel, impactsLabel]
    );
    const options = useMemo(
        () =>
            buildChartOptions(
                { spent: spentLabel, impacts: impactsLabel },
                currencySymbol
            ),
        [spentLabel, impactsLabel, currencySymbol]
    );

    return (
        <section className="mt-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-xs font-medium uppercase text-gray-700">
                {t(`${translationRoot}.distribution-title`)}
            </h2>
            <div className="mt-4 h-80 w-full">
                {isLoading ? (
                    <div
                        className="flex h-full animate-pulse items-center justify-center rounded bg-gray-100 text-gray-500"
                        role="status">
                        {t(
                            'sections.campaigns.new-campaign.wizard-screens.screens.loading-screens'
                        )}
                    </div>
                ) : points.length ? (
                    <Line data={data} options={options} />
                ) : (
                    <div className="flex h-full items-center justify-center rounded border border-gray-200 text-center text-sm text-gray-500">
                        {t(`${translationRoot}.distribution-empty`)}
                    </div>
                )}
            </div>
        </section>
    );
};

export default memo(ForecastDistributionChart);
